import { uuidModule } from "@/modules/uuid";
import { SessionModelData } from "@/store/sessions/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IMarkSessionSeenOperation } from "@/store/sync/operations/types";
import {
  OptimisticSyncUpdate,
  SyncCustomErrorData,
  SyncModelData,
} from "@/store/sync/types";

/**
 * Advances the session owner's cross-device read watermark. The optimistic
 * value is the payload's `seen_event_at` (the latest reply's
 * `locally_created_at`), NOT `committedAt` — the unread derivation compares
 * reply timestamps against the watermark, so both must share a clock domain.
 */
export class MarkSessionSeenOperation extends BaseSyncOperation<IMarkSessionSeenOperation> {
  get operationKind() {
    return "MARK_SESSION_SEEN" as const;
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const observable = await this.store.sessions.getAsync(this.payload.session_id);
    if (!observable) return [];

    const optimisticUpdate: OptimisticSyncUpdate<SessionModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.session_id,
        model_kind: "SESSION",
        model_version: observable.modelVersion,
        model_data: {
          ...observable.modelData,
          owner_last_seen_event_at: this.payload.seen_event_at,
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      },
    };

    return [optimisticUpdate];
  }

  async triggerRecompute() {
    await this.store.sessions.recompute(this.payload.session_id);
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.ignoreError(SyncErrorHandlingType.RetryWithLimit);
  }
}
